import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import React, { useCallback, useEffect, useMemo } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { HeadcountChange } from '../../types';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import {
  getSelectedAccount,
  updateProjectedHeadcount,
} from '../../redux/account';
import {
  fetchHeadcountChanges,
  getActiveId,
  getIsLoading,
  getHeadcountChanges,
  saveHeadcountChanges,
} from '../../redux/headcountChanges';

import _ from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import HeadcountChangeRow from './components/HeadcountChangeRow';
import { parseFormikErrors } from '../../utils';
import { PageWrapper } from '../../components';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';

export interface HeadcountChangeFormik {
  headcountChanges: HeadcountChange[];
}
interface Props {
  isModalOpen: boolean;
  handleCloseModal: () => void;
}

const EditHeadcountChangeModal: React.FC<Props> = ({
  isModalOpen,
  handleCloseModal,
}) => {
  const dispatch = useAppDispatch();
  const headcountChangesData = useAppSelector(getHeadcountChanges);
  const isLoading = useAppSelector(getIsLoading);
  const { projectId } = useParams();

  const initialValues: HeadcountChangeFormik = {
    headcountChanges: headcountChangesData,
  };

  const activeId = useAppSelector(getActiveId);
  const account = useAppSelector(getSelectedAccount);

  const validationSchema = yup.object().shape({
    headcountChanges: yup
      .array()
      .of(
        yup.object().shape({
          projectId: yup.string().required(),
          countChange: yup.number().notOneOf([0]).required('Value is required'),
          date: yup
            .date()
            .min(DateTime.now().plus({ days: 1 }).startOf('day').toJSDate())
            .required(),
          delete: yup.boolean().optional(),
        }),
      )
      .test(
        'unique-date-project',
        'Each project can have only one projected headcount entry per date.',
        (values) => {
          if (!values || values.length === 0) return true;
          const seen = new Set();
          return values?.every(({ date, projectId, delete: isDeleted }) => {
            if (!isDeleted) {
              const key = `${date}-${projectId}`;
              if (seen.has(key)) {
                return false;
              }
              seen.add(key);
            }
            return true;
          });
        },
      ),
  });

  const formik = useFormik<HeadcountChangeFormik>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const updatedValues: HeadcountChange[] = values.headcountChanges
        .map((headcountChange) => ({
          ...headcountChange,
          projectId:
            headcountChange?.tempProjectId || headcountChange.projectId,
          date: DateTime.fromISO(headcountChange.date).toFormat('yyyy-LL-dd'),
          countChange: Number(headcountChange.countChange),
        }))
        .sort((a, b) => (a.date > b.date ? 1 : -1));
      values.headcountChanges = updatedValues;
      dispatch(updateProjectedHeadcount(updatedValues));
      dispatch(saveHeadcountChanges({ headcountChanges: updatedValues }));
      resetForm();
    },
    validateOnMount: false,
    validateOnChange: false,
  });

  const headcountChangesGrouped: {
    [key: string]: HeadcountChange[];
  } = useMemo(() => {
    // Filter out soft deleted headcount changes
    const activeHeadcountChanges = formik.values.headcountChanges.filter(
      (headCountChange: HeadcountChange) => !headCountChange.delete,
    );

    // Separate new entries from saved entries
    const savedChanges = activeHeadcountChanges.filter(
      (headCountChange) => headCountChange.id, // Assuming `id` is defined for saved entries
    );

    const newChanges = activeHeadcountChanges.filter(
      (headCountChange) => !headCountChange.id, // Assuming `id` is undefined for new entries
    );

    // Group saved changes by projectId
    const groupedSavedChanges = _.groupBy(savedChanges, 'projectId');

    if (newChanges.length) groupedSavedChanges['new'] = newChanges;

    return groupedSavedChanges;
  }, [formik.values.headcountChanges]);

  useEffect(() => {
    if (activeId === (projectId || account?.id)) return;

    const projectIds = projectId
      ? [projectId]
      : account?.projects?.map((project) => project.id) || [];

    if (projectIds.length > 0 && !isLoading) {
      dispatch(
        fetchHeadcountChanges({
          projectIds,
          activeId: projectId ?? account?.id ?? '',
        }),
      );
    }
  }, [account, projectId, activeId, dispatch, isLoading]);

  const addProjectedHeadcount = useCallback(() => {
    formik.setValues((previousValue) => ({
      headcountChanges: [
        ...previousValue.headcountChanges,
        {
          projectId: projectId ?? null,
          date: '',
          countChange: 0,
          tempId: uuidV4(),
        },
      ],
    }));
  }, [formik, projectId]);

  const findIndex = useCallback(
    (id: string) => {
      return formik.values.headcountChanges.findIndex(
        (headcount) => headcount.id === id || headcount.tempId === id,
      );
    },
    [formik.values.headcountChanges],
  );

  const handleDeleteHeadcount = useCallback(
    (indexToBeDeleted?: number, id?: string) => {
      if (id) {
        formik.setFieldValue(
          `headcountChanges[${indexToBeDeleted}].delete`,
          true,
        );
      } else {
        formik.setFieldValue(
          'headcountChanges',
          formik.values.headcountChanges.filter(
            (_, headcountChangeIndex) =>
              headcountChangeIndex !== indexToBeDeleted,
          ),
        );
      }
    },
    [formik],
  );

  const handleDataChange = useCallback(
    (field: string, value: number | string | null, index: number) => {
      formik.setFieldValue(`headcountChanges[${index}].[${field}]`, value);
    },
    [formik],
  );

  const resetForm = useCallback(() => {
    formik.resetForm();
    handleCloseModal();
  }, [formik, handleCloseModal]);

  return (
    <Dialog
      fullWidth
      onClose={handleCloseModal}
      open={isModalOpen}
      scroll="body"
      maxWidth="md"
    >
      <DialogTitle>Edit projected headcount </DialogTitle>
      <CloseOutlinedIcon
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 22,
          top: 21,
          color: 'grey',
          cursor: 'pointer',
        }}
        onClick={resetForm}
        fontSize="small"
      ></CloseOutlinedIcon>

      <DialogContent sx={{ maxHeight: 600 }}>
        <PageWrapper isLoading={isLoading} sx={{ paddingBlock: 0 }}>
          {_.isEmpty(headcountChangesGrouped) && (
            <Typography variant="h4" color="text.secondary" pt={1}>
              No entries available for projected headcount changes.
            </Typography>
          )}

          {Object.keys(headcountChangesGrouped).map(
            (headcountChangeKey) =>
              headcountChangesGrouped[headcountChangeKey].length > 0 && (
                <HeadcountChangeRow
                  isSingleProjectSelected={!!projectId}
                  findIndex={findIndex}
                  key={headcountChangeKey}
                  headcountChangeKey={headcountChangeKey}
                  headcountChanges={headcountChangesGrouped[headcountChangeKey]}
                  handleDataChange={handleDataChange}
                  handleDeleteHeadcount={handleDeleteHeadcount}
                  projects={account?.projects}
                  errors={formik.errors}
                />
              ),
          )}

          <Box sx={{ px: projectId ? 0 : 2 }}>
            {formik.errors.headcountChanges && (
              <>
                {parseFormikErrors(formik.errors.headcountChanges).map(
                  (error) => (
                    <Typography key={error} color="error">
                      {error}
                    </Typography>
                  ),
                )}
              </>
            )}
          </Box>
        </PageWrapper>
      </DialogContent>

      <DialogActions
        sx={{ alignItems: 'stretch', justifyContent: 'space-between' }}
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Button onClick={addProjectedHeadcount}>Add entry</Button>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          justifyContent="space-between"
          gap={1}
        >
          <Button onClick={resetForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={formik.submitForm}
            disabled={!formik.dirty}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditHeadcountChangeModal;
